'use client';

import {
  JSX,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import classNames from 'classnames';

// Components
import { AtDivider } from '@/components/atoms/at-divider/at-divider';
import { AtVideoPlayer } from '@/components/atoms/at-video-player/at-video-player';
import { AtMedia } from '@/components/atoms/at-media/at-media';
import { AtPlayButton } from '@/components/atoms/at-play-button/at-play-button';
import { AtHeader } from '@/components/atoms/at-header/at-header';
import { AtText } from '@/components/atoms/at-text/at-text';

// Types
import type { IOrFeaturedVideoProps } from './or-featured-video.types';
import {
  EDividerColors,
  EDividerDirectionalVariants,
  EDividerVariants,
} from '@/components/atoms/at-divider/at-divider.types';
import type { IAtMediaProps } from '@/components/atoms/at-media/at-media.types';
import { EImageIntents } from '@/components/atoms/at-media/at-media.types';
import useBreakpoint from '@/hooks/useBreakpoint';

// Constants
const SPACING_CLASSES = {
  top: {
    xs: 'pt-xs',
    sm: 'pt-sm',
    md: 'pt-md',
    lg: 'pt-lg',
    xl: 'pt-xl',
  },
  bottom: {
    xs: 'pb-xs',
    sm: 'pb-sm',
    md: 'pb-md',
    lg: 'pb-lg',
    xl: 'pb-xl',
  },
} as const;

const DEFAULT_SPACING = { spacing_value: 'md' as const, divider: false };

/**
 * `OrFeaturedVideo` renders a responsive, autoplay-on-click video block
 * with optional title/description overlay. Optimized for performance
 * and accessibility.
 */
export const OrFeaturedVideo = ({
  description,
  playButtonAriaLabel,
  thumbnail,
  title,
  url,
  topSpacing = DEFAULT_SPACING,
  bottomSpacing = DEFAULT_SPACING,
}: IOrFeaturedVideoProps): JSX.Element => {
  const [showVideo, setShowVideo] = useState(false);
  const [client, setClient] = useState(false);
  const [isIntersecting, setIsIntersecting] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const containerClasses = useMemo(
    () =>
      classNames(
        SPACING_CLASSES.bottom[bottomSpacing.spacing_value],
        SPACING_CLASSES.top[topSpacing.spacing_value],
      ),
    [bottomSpacing.spacing_value, topSpacing.spacing_value],
  );

  const handlePlayVideo = useCallback(() => {
    setShowVideo(true);
  }, []);

  const overlayAltText = useMemo(() => {
    if (title && description) return `${title} ${description}`;
    return 'Image overlay for video about Schumacher Homes';
  }, [title, description]);

  const playButtonAriaLabelMemoized = useMemo(() => {
    return (
      playButtonAriaLabel || `Play video ${title || 'Schumacher Homes Video'}`
    );
  }, [playButtonAriaLabel, title]);

  useEffect(() => {
    setClient(true);
  }, []);

  // Intersection Observer
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsIntersecting(true);
          observer.disconnect(); // Only observe once
        }
      },
      { threshold: 0.2 },
    );

    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, []);

  const bp = useBreakpoint();

  const isDesktop = useMemo(() => {
    return ['lg', 'xl', '2xl', '3xl'].includes(bp);
  }, [bp]);

  const hasContent = Boolean(title || description);

  return (
    <section
      data-theme="light"
      className={containerClasses}
      data-testid="or-featured-video"
    >
      {topSpacing.divider && (
        <AtDivider
          decorative
          color={EDividerColors.GREY}
          direction={EDividerDirectionalVariants.HORIZONTAL}
          intent={EDividerVariants.THIN}
        />
      )}

      <div className="container">
        <div
          ref={containerRef}
          className="relative flex w-full items-center justify-center overflow-hidden rounded-30"
        >
          {client && (
            <>
              {!showVideo && (
                <div className="z-10">
                  <VideoOverlay
                    ariaLabel={playButtonAriaLabelMemoized}
                    alt={overlayAltText}
                    description={description}
                    thumbnail={thumbnail}
                    title={title}
                    onClick={handlePlayVideo}
                    isDesktop={isDesktop}
                    hasContent={hasContent}
                  />
                </div>
              )}
              <div className="relative aspect-video w-full">
                {isIntersecting && (
                  <AtVideoPlayer
                    data-testid="video-player"
                    containerClasses="absolute top-0 left-0 w-full h-full"
                    url={url}
                    playing={showVideo}
                    muted
                    controls
                  />
                )}
              </div>
            </>
          )}
        </div>

        {/* Mobile-only content below video */}
        {hasContent && (
          <div className="block text-balance pt-10 text-center lg:hidden">
            {title && (
              <AtHeader
                element="h2"
                variant="h1"
                className="pb-6 text-skin-media"
              >
                {title}
              </AtHeader>
            )}
            {description && (
              <AtText className="text-skin-media">{description}</AtText>
            )}
          </div>
        )}
      </div>

      {bottomSpacing.divider && (
        <AtDivider
          decorative
          color={EDividerColors.GREY}
          direction={EDividerDirectionalVariants.HORIZONTAL}
          intent={EDividerVariants.THIN}
        />
      )}
    </section>
  );
};

interface IVideoOverlayProps {
  ariaLabel?: string;
  thumbnail?: IAtMediaProps;
  title?: string;
  description?: string | ReactNode;
  onClick: () => void;
  alt: string;
  isDesktop: boolean;
  hasContent: boolean;
}

/**
 * `VideoOverlay` renders a clickable overlay over the video player
 * with optional heading, description, and background image.
 *
 * Fades out when the user initiates playback.
 */
const VideoOverlay = ({
  ariaLabel,
  description,
  thumbnail,
  title,
  onClick,
  alt,
  isDesktop,
  hasContent,
}: IVideoOverlayProps) => {
  if (!thumbnail) return null;

  return (
    <div
      className="absolute left-0 top-0 z-20 h-full w-full"
      data-testid="video-overlay"
    >
      <div className="relative aspect-video overflow-hidden rounded-3xl">
        <AtMedia
          alt={alt}
          className="aspect-video w-full"
          intent={EImageIntents.BACKGROUND}
          roundedCorners="3xl"
          src={thumbnail.src}
          sizes="100vw"
          aspectRatio={{ mobile: '16/9', tablet: '16/9', desktop: '16/9' }}
        />

        {/* Overlay filter */}
        <div className="pointer-events-none absolute inset-0 z-10 h-full w-full bg-video-overlay" />

        <div className="absolute inset-0 z-20 flex items-center justify-center">
          {hasContent && isDesktop ? (
            <ContentWithPlayButton
              title={title}
              description={description}
              playButtonAriaLabel={ariaLabel}
              onClick={onClick}
            />
          ) : (
            <AtPlayButton
              background={false}
              onClick={onClick}
              ariaLabel={ariaLabel}
            />
          )}
        </div>
      </div>
    </div>
  );
};

interface IContentWithPlayButtonProps {
  title?: string;
  description?: string | ReactNode;
  playButtonAriaLabel?: string;
  onClick: () => void;
}

const ContentWithPlayButton = ({
  title,
  description,
  playButtonAriaLabel,
  onClick,
}: IContentWithPlayButtonProps) => (
  <div className="relative z-20 hidden h-full w-full lg:flex lg:flex-row">
    <div className="mb-6 flex h-full w-full items-center justify-center">
      <AtPlayButton
        background={false}
        onClick={onClick}
        ariaLabel={playButtonAriaLabel}
      />
    </div>
    <div className="pointer-events-none text-balance px-5 text-center md:text-left lg:flex lg:flex-col lg:justify-center">
      {title && (
        <AtHeader
          element="h2"
          variant="h1"
          className="mb-2 hidden !text-skin-media lg:mb-6 lg:block"
        >
          {title}
        </AtHeader>
      )}
      {description && (
        <AtText className="!text-skin-media">{description}</AtText>
      )}
    </div>
  </div>
);

OrFeaturedVideo.VideoOverlay = VideoOverlay;
